import * as React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Box, Typography, IconButton, CircularProgress, Alert, Snackbar, Chip, Button, Collapse } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessUserRow from "../../components/admin/AccessUserRow";
import DeleteCustomerDataDialog from "../../components/admin/DeleteCustomerDataDialog";
import PortalPagination from "../../components/portal/PortalPagination";
import { getProgressStatusStyle } from "../../theme/statusColors";
import {
  fetchCustomerUsers,
  fetchCustomerOrders,
  updateUserAccess,
  removeUserOrderAccess,
  removeUserAccess,
  grantOrderAccess,
} from "../../Services/customerAccessService";

function InfoField({ label, value }) {
  return (
    <Box>
      <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.25 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "0.8125rem", color: "#111827" }}>{value || "—"}</Typography>
    </Box>
  );
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

function capitalize(value) {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

// Raw step_key with underscores turned into spaces - matches the admin
// order-detail page's preference for the technical status name over the
// patient-facing copy.
function rawStepLabel(key) {
  return (key || "").replace(/_/g, " ");
}

function CustomerOrderRow({ order, onClick }) {
  const [expanded, setExpanded] = React.useState(false);
  const testCount = (order.tests || []).length;

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: 1.5,
        p: 1.5,
        bgcolor: "#FFFFFF",
        cursor: "pointer",
        "&:hover": { bgcolor: "#F9FAFB", borderColor: "#d1d5db" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#111827" }}>{order.order_name}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
          <Typography sx={{ fontSize: "0.6875rem", color: "#9ca3af" }}>{formatDate(order.processed_at)}</Typography>
          <IconButton onClick={toggleExpanded} size="small" sx={{ p: 0.25 }}>
            <ExpandMoreIcon
              fontSize="small"
              sx={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}
            />
          </IconButton>
        </Box>
      </Box>

      {!expanded && (
        <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mt: 0.75 }}>
          {testCount} {testCount === 1 ? "test" : "tests"}
        </Typography>
      )}

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 1 }}>
          {(order.tests || []).map((test) => (
            <Box
              key={test.lab_test_id}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
            >
              <Typography sx={{ fontSize: "0.75rem", color: "#374151", minWidth: 0 }} noWrap>
                {test.substrate}
              </Typography>
              <Chip
                size="small"
                label={rawStepLabel(test.current_step_key)}
                sx={{
                  ...getProgressStatusStyle(test.current_step_key),
                  fontSize: "0.625rem",
                  height: "20px",
                  flexShrink: 0,
                  "& .MuiChip-label": { px: 0.75 },
                }}
              />
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// No GET /admin/customers/:id endpoint exists - identity fields come from
// router state (the Customers-tab row, or Order Detail's customer card,
// both of which already have the full customer object). A direct URL
// load/refresh just shows "—" for those fields; the linked-users list
// below still loads fine since it's fetched by :customerId regardless.
function CustomerDetail() {
  const { customerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const customer = location.state?.customer || null;

  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [orders, setOrders] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(true);
  const [ordersError, setOrdersError] = React.useState("");
  const [ordersPage, setOrdersPage] = React.useState(1);
  const [ordersPagination, setOrdersPagination] = React.useState(null);

  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });

  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deletionResult, setDeletionResult] = React.useState(null);

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  const load = React.useCallback(() => {
    setLoading(true);
    setError("");
    fetchCustomerUsers(customerId)
      .then((res) => setUsers(res?.data?.users || []))
      .catch((err) => setError(err.message || "Failed to load linked users."))
      .finally(() => setLoading(false));
  }, [customerId]);

  React.useEffect(() => {
    let cancelled = false;
    setOrdersLoading(true);
    setOrdersError("");
    fetchCustomerOrders(customerId, { page: ordersPage, limit: 10 })
      .then((res) => {
        if (cancelled) return;
        setOrders(res?.data?.orders || []);
        setOrdersPagination(res?.data?.pagination || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setOrdersError(err.message || "Failed to load this customer's orders.");
      })
      .finally(() => {
        if (!cancelled) setOrdersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, ordersPage]);

  React.useEffect(() => {
    load();
  }, [load]);

  // Label lookup for tests shown in the access panel - built from this
  // customer's already-fetched orders. Known limitation: `orders` is
  // paginated at 10/page, so a test on an order outside the current page
  // falls back to AccessUserRow's shortened-id label instead.
  const testsById = React.useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      (order.tests || []).forEach((test) => {
        map[test.lab_test_id] = test;
      });
    });
    return map;
  }, [orders]);

  const handlePatch = async (userId, patch) => {
    const res = await updateUserAccess(customerId, userId, patch);
    const updated = res?.data || {};
    setUsers((prev) =>
      prev.map((u) =>
        u.user_id === userId
          ? {
              ...u,
              has_access: updated.has_access ?? u.has_access,
              receives_emails: updated.receives_emails ?? u.receives_emails,
              scope: updated.scope ?? u.scope,
              order_ids: updated.order_ids ?? u.order_ids,
            }
          : u
      )
    );
    notify("Access updated.");
  };

  const handleRemoveOrder = async (userId, orderId, testIds) => {
    const res = await removeUserOrderAccess(customerId, userId, orderId, testIds);
    const data = res?.data || {};
    setUsers((prev) =>
      data.grant_removed
        ? prev.filter((u) => u.user_id !== userId)
        : prev.map((u) => (u.user_id === userId ? { ...u, order_ids: data.order_ids ?? [] } : u))
    );
    notify(testIds && testIds.length > 0 ? "Test removed from this grant." : "Order removed from this grant.");
  };

  const handleUpdateTestAccess = async (email, orderId, testId, patch) => {
    await grantOrderAccess(orderId, {
      email,
      hasAccess: patch.hasAccess,
      receivesEmails: patch.receivesEmails,
      testIds: [testId],
    });
    notify("Test access updated.");
    await load();
  };

  const handleRemoveAll = async (userId) => {
    await removeUserAccess(customerId, userId);
    setUsers((prev) => prev.filter((u) => u.user_id !== userId));
    notify("Access removed.");
  };

  const displayName = customer ? [customer.first_name, customer.last_name].filter(Boolean).join(" ") : "";

  const cardHeaderSx = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    mb: 1.5,
  };
  const cardSx = { border: "1px solid #e5e7eb", borderRadius: 2, p: 2.5, bgcolor: "#FFFFFF" };

  return (
    <Box sx={{ p: 2, maxWidth: 1200 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <PeopleAltIcon sx={{ fontSize: "1.5rem", color: "#333" }} />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Customer Details
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 340px" },
          gridAutoRows: "auto",
          alignItems: "stretch",
          gap: 2.5,
        }}
      >
        {/* Left/top: identity */}
        <Box sx={cardSx}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: "#111827", mb: 1.5 }}>
            {displayName || customer?.email || "Customer"}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" }, gap: 2 }}>
            <InfoField label="Email" value={customer?.email} />
            <InfoField label="Phone" value={customer?.phone} />
            <InfoField label="State" value={capitalize(customer?.state)} />
            <InfoField label="Joined" value={formatDate(customer?.created_at)} />
          </Box>
        </Box>

        {/* Right/top: destructive data-deletion action */}
        <Box sx={{ ...cardSx, display: "flex", flexDirection: "column" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#b91c1c", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
            Danger zone
          </Typography>

          {deletionResult ? (
            <Alert severity="success" sx={{ fontSize: "0.75rem" }}>
              Customer data deleted.
            </Alert>
          ) : (
            <>
              <Typography sx={{ fontSize: "0.75rem", color: "#6b7280", mb: 1.5 }}>
                Permanently erases this customer's portal data (form submissions, lab results, files). Their orders
                and Shopify record are kept. This cannot be undone.
              </Typography>
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                color="error"
                variant="outlined"
                size="small"
                startIcon={<DeleteForeverIcon fontSize="small" />}
                sx={{ alignSelf: "flex-start" }}
              >
                Delete customer data
              </Button>
            </>
          )}
        </Box>

        {/* Left/bottom: linked users (access management) */}
        <Box sx={cardSx}>
          <Typography sx={cardHeaderSx}>Linked users</Typography>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          )}
          {!loading && error && <Alert severity="error">{error}</Alert>}
          {!loading && !error && users.length === 0 && (
            <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af" }}>No one has access yet.</Typography>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {!loading &&
              !error &&
              users.map((user) => (
                <AccessUserRow
                  key={user.user_id}
                  user={user}
                  context="customer"
                  onPatch={handlePatch}
                  onRemoveOrder={handleRemoveOrder}
                  onRemoveAll={handleRemoveAll}
                  onUpdateTestAccess={handleUpdateTestAccess}
                  testsById={testsById}
                />
              ))}
          </Box>
        </Box>

        {/* Right/bottom: this customer's orders - click through to grant access from one.
            flex column + mt:"auto" on the pagination wrapper keeps the page
            numbers pinned to the card's bottom edge regardless of how many
            orders are on the current page (this card stretches to match the
            "Linked users" card's height via the grid's alignItems: stretch). */}
        <Box sx={{ ...cardSx, display: "flex", flexDirection: "column" }}>
          <Typography sx={cardHeaderSx}>Linked orders</Typography>

          {ordersLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!ordersLoading && ordersError && <Alert severity="error">{ordersError}</Alert>}
          {!ordersLoading && !ordersError && orders.length === 0 && (
            <Typography sx={{ fontSize: "0.8125rem", color: "#9ca3af" }}>No orders yet.</Typography>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {!ordersLoading &&
              !ordersError &&
              orders.map((order) => (
                <CustomerOrderRow
                  key={order.order_id}
                  order={order}
                  onClick={() => navigate(`/orders/${order.order_id}`)}
                />
              ))}
          </Box>

          {!ordersLoading && !ordersError && (
            <Box sx={{ mt: "auto" }}>
              <PortalPagination pagination={ordersPagination} onChange={setOrdersPage} />
            </Box>
          )}
        </Box>
      </Box>

      <DeleteCustomerDataDialog
        open={deleteDialogOpen}
        customerId={customerId}
        customerLabel={displayName || customer?.email}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={(result) => {
          setDeletionResult(result);
          setDeleteDialogOpen(false);
          notify("Customer data deleted.");
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CustomerDetail;
